import dvaModelExtend from 'dva-model-extend';
import _ from 'lodash';
import { getIntl, history } from 'umi';

import { bindServices } from '@/services/api';
import { createCrudModel } from '@/utils/core/dva';
import { CoreEnums } from '@/utils/domain/constants';
import { getFiltersGlobalFilters } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';

import { ENTITY_NAME, FETCH_ROUTE, MODEL_NAME } from './constants';
import {
  buildConfigPowerBIReport,
  buildReportIdentityPayload,
  hydratePowerBIReportItem,
  hydrateReportForRuntime,
  isNativeProvider,
  isPowerBIProvider,
  unwrapReportEntity,
} from './reportContract';

const api = bindServices('reports');
const { BulkActionType } = CoreEnums;

const namespace = MODEL_NAME;
const fetchRoute = FETCH_ROUTE;
const fetchParams = 'ManageReports';

const processPermissionItem = (item, field) => {
  const subItem = item[`${field}Id`] !== undefined ? item[field] : item;
  const itemId = item[`${field}Id`] || item.id || subItem?.id;
  return {
    ...subItem,
    id: itemId,
    name: subItem?.name ?? subItem?.fullName ?? subItem?.displayName ?? subItem?.description,
    fullName: subItem?.fullName ?? subItem?.name,
  };
};

const _model = createCrudModel({
  namespace,
  fetchRoute,
  fetchParams,
  entityName: ENTITY_NAME,
  api,
  features: { import: false, bulkActions: false },
  state: {
    showPermissions: false,
    permissionItem: null,
    userListPermission: null,
    positionListPermission: null,
    userGroupListPermission: null,
    areaListPermission: null,
    fetchingPermissionList: null,
    permissionsCache: {},
  },
  effects: {
    *fetchItem({ payload }, { call, put, select }) {
      try {
        yield put({ type: 'success', payload: false });
        const modelState = yield select((state) => state[namespace] || {});
        const targetId = String(payload.id || '').toLowerCase();
        const listed = (modelState.list || []).find(
          (row) => String(row?.id || '').toLowerCase() === targetId,
        );
        const provider =
          payload.provider ??
          modelState.openedProvider ??
          listed?.provider ??
          modelState.item?.provider;
        let item;

        if (payload.id && provider != null && provider !== '' && isPowerBIProvider(provider)) {
          const configRes = yield call(api.apiRoute, {
            route: `report/config/powerBI/${payload.id}`,
          });
          item = hydratePowerBIReportItem(payload.id, configRes);
        } else {
          const res = yield call(api.getById, {
            route: fetchRoute,
            id: payload.id,
            params: payload.detail
              ? CoreQueries[fetchParams]?.itemDetail
              : { ...CoreQueries[fetchParams]?.item, ...(payload?.params && { ...payload.params }) },
          });
          item = hydrateReportForRuntime(unwrapReportEntity(res));
        }
        yield put({ type: 'queryItem', payload: item });
      } catch (e) {}
    },

    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const data = buildReportIdentityPayload(payload.data || {});
        const res = yield call(api.create, { route: fetchRoute, data });
        const created = unwrapReportEntity(res) || res?.data || res;
        let configError = null;
        if (created?.id && isPowerBIProvider(data.provider)) {
          try {
            yield call(api.apiRoute, {
              route: `report/config/powerBI/${created.id}`,
              method: 'post',
              data: buildConfigPowerBIReport(payload.data || {}),
            });
          } catch (e) {
            configError = e;
          }
        }
        yield put({ type: 'success', payload: { success: true } });
        if (configError) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'warning',
              message:
                configError.message ||
                getIntl().formatMessage({ id: 'reports.form.powerBiConfig.saveError' }),
            },
          });
        } else {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
        const createdId = created?.id || created?.data?.id;
        if (createdId && isNativeProvider(data.provider)) {
          history.push(`/reports/${createdId}/builder`);
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors || [],
          });
          yield put({
            type: 'errors',
            payload: { validationErrors: e.validationErrors || [], message: e.message },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const data = buildReportIdentityPayload(payload.data || {});
        yield call(api.update, { route: fetchRoute, data });
        let configError = null;
        if (data.id && isPowerBIProvider(data.provider)) {
          try {
            yield call(api.apiRoute, {
              route: `report/config/powerBI/${data.id}`,
              method: 'post',
              data: buildConfigPowerBIReport(payload.data || {}),
            });
          } catch (e) {
            configError = e;
          }
        }
        yield put({ type: 'success', payload: { success: true } });
        if (configError) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'warning',
              message:
                configError.message ||
                getIntl().formatMessage({ id: 'reports.form.powerBiConfig.saveError' }),
            },
          });
        } else {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'success' },
          });
        }
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors || [],
          });
          yield put({
            type: 'errors',
            payload: { validationErrors: e.validationErrors || [], message: e.message },
          });
        }
      }
    },

    *createNativeReport({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const data = buildReportIdentityPayload({
          ...(payload.data || {}),
          provider: CoreEnums.ReportProvider.Native,
        });
        const res = yield call(api.create, { route: fetchRoute, data });
        const created = res?.data || res;
        const createdId = created?.id || created?.data?.id;
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'success', payload: { success: true } });
        if (createdId) {
          history.push(`/reports/${createdId}/builder`);
        }
        return createdId ? { ...created, id: createdId } : created;
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
            validationErrors: e.validationErrors || [],
          });
        }
      }
      return null;
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (!_.isNil(payload.id) && payload.item) {
        yield put({ type: 'editOrDetail', payload });
      }
    },

    *bulkDeleteReports({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const pageSize = yield select((state) => state.global.pageSize);
        const stateData = yield select((state) => state[namespace]);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams]?.paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(stateData.orderBy && { orderBy: stateData.orderBy }),
          ...(filters && { filter: filters }),
          select: stateData.select,
        };

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkActivateReports({ payload }, { call, put }) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Activate,
          ids,
        });
        yield put({ type: 'bulkReportActivate', payload: { ids, isActive: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *bulkDeactivateReports({ payload }, { call, put }) {
      const { ids } = payload;
      if (!ids || ids.length === 0) return;
      yield put({ type: 'queryActivatingItem', payload: { ids, active: true } });
      try {
        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Deactivate,
          ids,
        });
        yield put({ type: 'bulkReportActivate', payload: { ids, isActive: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      } finally {
        yield put({ type: 'queryActivatingItem', payload: { ids, active: false } });
      }
    },

    *showPermissions({ payload }, { put, call, select }) {
      yield put({ type: 'queryShowPermissions', payload });
      if (payload?.state !== false) return;
      yield call(() => new Promise((resolve) => setTimeout(resolve, 500)));
      const stillClosed = !(yield select((state) => state[namespace].showPermissions));
      if (stillClosed) {
        yield put({ type: 'queryHidePermission' });
      }
    },

    *fetchListPermissions({ payload }, { call, put, select }) {
      const { route, params, name, id } = payload;
      let trackingFetch = false;
      try {
        const currentState = yield select((state) => state[namespace]);
        const fieldMap = {
          userListPermission: 'user',
          positionListPermission: 'position',
          userGroupListPermission: 'userGroup',
          areaListPermission: 'area',
        };
        const field = fieldMap[name] || name;
        const cacheKey = `${id}_${field}`;

        if (currentState[name] !== null && currentState[name] !== undefined) return;

        if (currentState.permissionsCache?.[cacheKey] !== undefined) {
          yield put({
            type: 'queryListPermissions',
            payload: { [name]: currentState.permissionsCache[cacheKey] },
          });
          return;
        }

        trackingFetch = true;
        yield put({ type: 'queryFetchingPermissionList', payload: name });

        const data = yield call(api.getAll, { route, params });
        const processedData = (data || []).map((item) => processPermissionItem(item, field));

        yield put({
          type: 'cachePermissionsData',
          payload: { key: cacheKey, data: processedData },
        });
        yield put({ type: 'queryListPermissions', payload: { [name]: processedData } });
      } catch (e) {
        /*  */
      } finally {
        if (trackingFetch) {
          yield put({ type: 'queryFetchingPermissionList', payload: null });
        }
      }
    },

    *savePermission({ payload }, { call, put, select }) {
      try {
        const { id } = payload;
        let currentState = yield select((state) => state[namespace]);
        const permissionsCache = currentState.permissionsCache || {};

        const permissionConfigs = [
          {
            name: 'userListPermission',
            type: 'user',
            route: `report/allowedUsers/${id}`,
            params: {
              select: 'id,fullName',
              expand: { position: { select: 'name' } },
              orderBy: 'fullName',
            },
          },
          {
            name: 'positionListPermission',
            type: 'position',
            route: `report/allowedPositions/${id}`,
            params: { select: 'id,name,description', orderBy: 'name' },
          },
          {
            name: 'areaListPermission',
            type: 'area',
            route: `report/allowedAreas/${id}`,
            params: { select: 'id,name,description', orderBy: 'name' },
          },
          {
            name: 'userGroupListPermission',
            type: 'userGroup',
            route: `report/allowedUserGroups/${id}`,
            params: { select: 'id,name,description', orderBy: 'name' },
          },
        ];

        const tabsToLoad = permissionConfigs.filter(
          (config) => permissionsCache[`${id}_${config.type}`] === undefined,
        );

        if (tabsToLoad.length > 0) {
          for (const config of tabsToLoad) {
            const cacheKey = `${id}_${config.type}`;
            const data = yield call(api.getAll, { route: config.route, params: config.params });
            const processedData = (data || []).map((item) =>
              processPermissionItem(item, config.type),
            );

            yield put({
              type: 'cachePermissionsData',
              payload: { key: cacheKey, data: processedData },
            });
            currentState = yield select((state) => state[namespace]);
            const currentLocalState = currentState[config.name];
            if (
              currentLocalState === null ||
              (Array.isArray(currentLocalState) && currentLocalState.length === 0)
            ) {
              yield put({
                type: 'queryListPermissions',
                payload: { [config.name]: processedData },
              });
            }
          }
        }

        currentState = yield select((state) => state[namespace]);
        const saveData = {
          users: (currentState.userListPermission || []).map((l) => ({ id: l.id })),
          positions: (currentState.positionListPermission || []).map((l) => ({ id: l.id })),
          userGroups: (currentState.userGroupListPermission || []).map((l) => ({ id: l.id })),
          areas: (currentState.areaListPermission || []).map((l) => ({ id: l.id })),
        };

        yield call(api.apiRoute, {
          route: `${fetchRoute}/updatePermissions/${id}`,
          method: 'post',
          data: saveData,
        });

        const permissionLists = {
          userListPermission: 'user',
          positionListPermission: 'position',
          areaListPermission: 'area',
          userGroupListPermission: 'userGroup',
        };

        currentState = yield select((state) => state[namespace]);
        for (const listName of Object.keys(permissionLists)) {
          const permissionType = permissionLists[listName];
          const cacheKey = `${id}_${permissionType}`;
          const currentList = currentState[listName] || [];
          yield put({
            type: 'cachePermissionsData',
            payload: { key: cacheKey, data: currentList },
          });
        }

        yield put({
          type: 'showPermissions',
          payload: { state: false, item: null },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *addListPermission({ payload }, { put }) {
      yield put({ type: 'queryAddListPermission', payload });
    },

    *deleteItemPermission({ payload }, { put }) {
      yield put({ type: 'queryDeleteItemPermission', payload });
    },
  },
  reducers: {
    bulkReportActivate(state, { payload }) {
      const { ids, isActive } = payload;
      const idSet = new Set(ids);
      const list = state.list.map((item) => (idSet.has(item.id) ? { ...item, isActive } : item));
      return { ...state, list };
    },

    queryShowPermissions(state, { payload }) {
      return {
        ...state,
        showPermissions: payload.state,
        permissionItem: payload.item ?? null,
      };
    },

    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        detail: payload.detail,
        id: payload.id,
        item: payload.item ?? null,
        openedProvider: payload.state ? payload.provider : undefined,
        success: false,
        validationErrors: [],
      };
    },

    editOrDetail(state, { payload }) {
      const item = payload.item ?? {};
      return {
        ...state,
        item: {
          ...item,
          permissionsFiltersLogicalOperator:
            item.permissionsFiltersLogicalOperator ?? CoreEnums.LogicalOperator.Or,
        },
      };
    },

    queryItem(state, { payload }) {
      return {
        ...state,
        item: {
          ...payload,
          permissionsFiltersLogicalOperator:
            payload?.permissionsFiltersLogicalOperator ?? CoreEnums.LogicalOperator.Or,
        },
      };
    },

    queryListPermissions(state, { payload }) {
      return {
        ...state,
        userListPermission: payload.userListPermission ?? state.userListPermission,
        positionListPermission: payload.positionListPermission ?? state.positionListPermission,
        userGroupListPermission: payload.userGroupListPermission ?? state.userGroupListPermission,
        areaListPermission: payload.areaListPermission ?? state.areaListPermission,
      };
    },

    queryAddListPermission(state, { payload }) {
      return { ...state, [payload.name]: [...(state[payload.name] || []), payload.data] };
    },

    queryDeleteItemPermission(state, { payload }) {
      return {
        ...state,
        [payload.name]: _.filter(state[payload.name] || [], (l) => l.id !== payload.id),
      };
    },

    queryHidePermission(state) {
      return {
        ...state,
        showPermissions: false,
        permissionItem: null,
        id: null,
        userListPermission: null,
        positionListPermission: null,
        userGroupListPermission: null,
        areaListPermission: null,
        fetchingPermissionList: null,
        permissionsCache: {},
      };
    },

    queryFetchingPermissionList(state, { payload }) {
      return {
        ...state,
        fetchingPermissionList: payload ?? null,
      };
    },

    cachePermissionsData(state, { payload }) {
      const { key, data } = payload;
      return { ...state, permissionsCache: { ...state.permissionsCache, [key]: data } };
    },
  },
});

export default dvaModelExtend(_model, { namespace });
